import { ref, onMounted, onUnmounted } from 'vue'

export function useGameTimer() {
  const showGameModal = ref(false)
  const timeRemaining = ref(60) // Changer ici pour définir la durée du timer en secondes
  const timer = ref(null)

  const games = [
    { route: '/jeune/roue-des-competences', endpoint: '/games/skills' },
    { route: '/jeune/scenarios', endpoint: '/games/scenario' },
    { route: '/jeune/environment', endpoint: '/games/room-env' },
    { route: '/jeune/metiers', endpoint: '/games/jobs' },
    { route: '/jeune/shape-sequence-game', endpoint: '/games/shape-sequence' },
    { route: '/jeune/game-speed', endpoint: '/games/speed' },
  ]

  const getFallbackRandomRoute = () => {
    const randomIndex = Math.floor(Math.random() * games.length)
    return games[randomIndex].route
  }

  const startTimer = () => {
    // Prevent starting multiple timers
    if (timer.value) {
      return
    }

    timer.value = setInterval(() => {
      timeRemaining.value--

      if (timeRemaining.value <= 0) {
        clearInterval(timer.value)
        timer.value = null
        showGameModal.value = true
      }
    }, 1000)
  }

  const stopTimer = () => {
    if (timer.value) {
      clearInterval(timer.value)
      timer.value = null
    }
  }

  const resetTimer = () => {
    stopTimer()
    timeRemaining.value = 60 // Changer ici pour définir la durée du timer en secondes
    showGameModal.value = false
  }

  const getRandomGameRoute = async () => {
    try {
      // Importer dynamiquement AuthService pour éviter les dépendances circulaires
      const AuthService = (await import('@/services/AuthService.js')).default

      // Si l'utilisateur n'est pas authentifié, éviter les appels API inutiles
      // et retourner directement un jeu aléatoire
      if (!AuthService.getAccessToken()) {
        return getFallbackRandomRoute()
      }

      // Charger la progression de chaque jeu
      const gamesWithProgress = await Promise.all(
        games.map(async (game) => {
          try {
            const response = await AuthService.fetchWithAuth({
              method: 'get',
              url: game.endpoint,
            })
            return {
              ...game,
              completion: response.data.completion || 0,
            }
          } catch (error) {
            // Jeu non commencé, completion = 0
            return {
              ...game,
              completion: 0,
            }
          }
        })
      )

      // 1. Prioriser les jeux non commencés (completion = 0)
      const notStarted = gamesWithProgress.filter(g => g.completion === 0)
      if (notStarted.length > 0) {
        const randomIndex = Math.floor(Math.random() * notStarted.length)
        return notStarted[randomIndex].route
      }

      // 2. Ensuite les jeux en cours (0 < completion < 1)
      const inProgress = gamesWithProgress.filter(g => g.completion > 0 && g.completion < 1)
      if (inProgress.length > 0) {
        // Trier par progression décroissante (les plus avancés d'abord)
        inProgress.sort((a, b) => b.completion - a.completion)
        return inProgress[0].route
      }

      // 3. Si tous les jeux sont terminés, choisir au hasard
      return getFallbackRandomRoute()
    } catch (error) {
      console.error('Erreur lors de la sélection du jeu:', error)
      // En cas d'erreur, retourner un jeu au hasard
      return getFallbackRandomRoute()
    }
  }

  const closeModal = () => {
    showGameModal.value = false
    // Redémarrer le timer
    resetTimer()
    startTimer()
  }

  // Démarrer automatiquement le timer quand le composable est utilisé
  onMounted(() => {
    startTimer()
  })

  // Nettoyer le timer quand le composant est démonté
  onUnmounted(() => {
    stopTimer()
  })

  return {
    showGameModal,
    timeRemaining,
    startTimer,
    stopTimer,
    resetTimer,
    getRandomGameRoute,
    closeModal,
  }
}
